// Mirrors server/Modules/Pluspoint.Modules.Core/Locations/Services/LocationCompletenessService.cs
// (ScoreCompleteness + BuildCompletenessSnapshot). Backend is authoritative on save — any rule change
// must land in both places. Social presence is counted from the Location.SocialLinks fields.

import { LocationType } from '@constants/companies';
import { isPersistedPhoneInput } from '@helpers/phone';
import { hasFutureSpecialHour, hasOpenDay, hasPublishedWorkingHours } from '@helpers/companies';

// Location.SocialLinks field name -> social platform key. Social presence is driven by these stored
// fields (mirrors the backend's AddSocialLinkPlatforms), not by profile rows.
const SOCIAL_PLATFORM_BY_SOCIAL_LINK_FIELD = {
    facebook: 'facebook',
    instagram: 'instagram',
    x: 'x',
    linkedIn: 'linkedin',
    pinterest: 'pinterest',
    youTube: 'youtube',
    tikTok: 'tiktok',
};

// Banker's rounding to match C# Math.Round's MidpointRounding.ToEven default.
const ROUND_EPSILON = 1e-9;
const roundHalfToEven = n => {
    if (n < 0) return -roundHalfToEven(-n);
    const floor = Math.floor(n);
    const frac = n - floor;
    if (frac < 0.5 - ROUND_EPSILON) return floor;
    if (frac > 0.5 + ROUND_EPSILON) return floor + 1;
    return floor % 2 === 0 ? floor : floor + 1;
};

const isHealthcareProvider = type => type === LocationType.HealthcareProfessional;

const isNonEmpty = value => typeof value === 'string' && value.trim().length > 0;

const countCategories = formValues => {
    const primary = (formValues.primaryCategory || []).filter(Boolean).length > 0 ? 1 : 0;
    const secondary = (formValues.secondaryCategories || []).filter(
        s => Array.isArray(s?.category) && s.category.length > 0 && s.category[0]?.id,
    ).length;
    return primary + secondary;
};

const collectSocialPlatforms = formValues => {
    const platforms = new Set();
    for (const [field, key] of Object.entries(SOCIAL_PLATFORM_BY_SOCIAL_LINK_FIELD)) {
        if (isNonEmpty(formValues.socialLinks?.[field])) platforms.add(key);
    }
    return platforms;
};

const SOCIAL_TIERS = [
    [1, 3],
    [2, 6],
    [3, 8],
    [4, 10],
    [5, 12],
];
const SERVICES_TIERS = [
    [1, 3],
    [3, 5],
];
const LONG_DESCRIPTION_TIERS = [
    [50, 3],
    [150, 6],
    [300, 8],
];
const categoriesTiers = max => [
    [1, roundHalfToEven(max * 0.6)],
    [3, max],
];

const tierPoints = (value, tiers) =>
    tiers.reduce((points, [threshold, tierValue]) => (value >= threshold ? tierValue : points), 0);

const nextTierPoints = (earned, tiers) => tiers.find(([, points]) => points > earned)?.[1] ?? earned;

const firstMissing = (formValues, fields) => fields.find(field => !isNonEmpty(formValues[field]));

const scoreBuckets = formValues => {
    const buckets = [];
    const bucket = (key, earned, max, details = {}) =>
        buckets.push({ key, earned: Math.min(Math.max(earned, 0), max), max, earnedAfterNextStep: max, ...details });

    const isHealthcare = isHealthcareProvider(formValues.type);

    bucket('locationName', isNonEmpty(formValues.locationName) ? 5 : 0, 5, {
        section: 'general',
        field: 'locationName',
    });

    if (isHealthcare) {
        const practitionerFields = ['firstName', 'lastName', 'headshotUrl'];
        let practitioner = practitionerFields.filter(field => isNonEmpty(formValues[field])).length;
        const hasDegree = formValues.degreeId !== '' && formValues.degreeId != null;
        if (hasDegree) practitioner += 1;
        const missing = firstMissing(formValues, ['firstName', 'lastName']) ?? (hasDegree ? 'headshotUrl' : 'degreeId');
        bucket('practitioner', practitioner, 4, {
            section: missing === 'headshotUrl' ? 'appearance' : 'general',
            field: missing,
        });

        const hasCertification = (formValues.certifications || []).some(c => isNonEmpty(c?.name));
        bucket('certifications', hasCertification ? 2 : 0, 2, { section: 'general', field: 'certifications.0.name' });
    }

    // Backend awards for any non-blank persisted PhoneNumber, but mapCompanyToFormValues
    // initializes an empty persisted phone to the locale country-code stub. Excluding the
    // stub via length > 4 keeps the live score aligned with what the backend will save
    // (the form's phone validation rejects stub-only submissions).
    bucket('phone', isPersistedPhoneInput(formValues.phoneNumber) ? 10 : 0, 10, {
        section: 'contact',
        field: 'phoneNumber',
    });
    bucket('email', isNonEmpty(formValues.email) ? 2 : 0, 2, { section: 'contact', field: 'email' });
    bucket('website', isNonEmpty(formValues.website) ? 5 : 0, 5, { section: 'contact', field: 'website' });

    if (formValues.hasBusinessAddress) {
        let address = 0;
        if (isNonEmpty(formValues.addressLine1)) address += 5;
        if (isNonEmpty(formValues.city)) address += 5;
        if (isNonEmpty(formValues.country)) address += 4;
        bucket('address', address, 14, {
            section: 'address',
            field: firstMissing(formValues, ['addressLine1', 'city', 'country']),
        });
    }

    if (formValues.workingHoursEnabled) {
        bucket('workingHours', hasOpenDay(formValues.workingHours) ? 10 : 0, 10, { section: 'businessHours' });
    }

    bucket('specialHours', hasFutureSpecialHour(formValues.specialHours) ? 3 : 0, 3, {
        section: 'specialHours',
        withoutAi: !hasPublishedWorkingHours(formValues.workingHoursEnabled, formValues.workingHours),
    });

    bucket('shortDescription', isNonEmpty(formValues.shortDescription) ? 3 : 0, 3, {
        section: 'additionalInfo',
        field: 'shortDescription',
    });
    bucket('mediumDescription', isNonEmpty(formValues.mediumDescription) ? 4 : 0, 4, {
        section: 'additionalInfo',
        field: 'mediumDescription',
    });

    const longLen = (formValues.longDescription || '').length;
    const longEarned = tierPoints(longLen, LONG_DESCRIPTION_TIERS);
    bucket('longDescription', longEarned, 8, {
        section: 'additionalInfo',
        field: 'longDescription',
        earnedAfterNextStep: nextTierPoints(longEarned, LONG_DESCRIPTION_TIERS),
        variant: longLen > 0 ? 'extend' : 'add',
    });

    bucket('openingDate', formValues.openingDate ? 1 : 0, 1, { section: 'additionalInfo', field: 'openingDate' });

    const hasEngagement =
        isNonEmpty(formValues.featuredMessage) ||
        isNonEmpty(formValues.featuredMessageUrl) ||
        (isPersistedPhoneInput(formValues.chatLink) && formValues.chatType != null);
    bucket('engagement', hasEngagement ? 1 : 0, 1, { section: 'additionalInfo' });

    const categoriesMax = isHealthcare ? 8 : 12;
    const categoryCount = countCategories(formValues);
    const categoryTiers = categoriesTiers(categoriesMax);
    const categoriesEarned = tierPoints(categoryCount, categoryTiers);
    bucket('categories', categoriesEarned, categoriesMax, {
        section: 'services',
        field: categoryCount === 0 ? 'primaryCategory' : undefined,
        earnedAfterNextStep: nextTierPoints(categoriesEarned, categoryTiers),
        variant: categoryCount === 0 ? 'add' : 'more',
    });

    const serviceCount = (formValues.services || []).filter(s => isNonEmpty(s?.name)).length;
    const servicesEarned = tierPoints(serviceCount, SERVICES_TIERS);
    bucket('services', servicesEarned, 5, {
        section: 'services',
        earnedAfterNextStep: nextTierPoints(servicesEarned, SERVICES_TIERS),
        variant: serviceCount === 0 ? 'add' : 'more',
    });

    const hasPaymentMethod = (formValues.paymentMethods || []).some(p => p?.value);
    bucket('paymentMethods', hasPaymentMethod ? 1 : 0, 1, { section: 'paymentMethods' });

    bucket('logo', isNonEmpty(formValues.logoUrl) ? 4 : 0, 4, { section: 'appearance' });
    bucket('cover', isNonEmpty(formValues.coverUrl) ? 4 : 0, 4, { section: 'appearance' });

    const socialCount = collectSocialPlatforms(formValues).size;
    const socialEarned = tierPoints(socialCount, SOCIAL_TIERS);
    bucket('social', socialEarned, 12, {
        section: 'socialLinks',
        earnedAfterNextStep: nextTierPoints(socialEarned, SOCIAL_TIERS),
        variant: socialCount === 0 ? 'add' : 'more',
    });

    return buckets;
};

export const calculateScore = formValues => {
    if (!formValues) return { earned: 0, max: 0, percent: 0, recommendations: [] };

    const buckets = scoreBuckets(formValues);
    const earned = buckets.reduce((sum, bucket) => sum + bucket.earned, 0);
    const max = buckets.reduce((sum, bucket) => sum + bucket.max, 0);
    const percent = max === 0 ? 0 : roundHalfToEven((earned * 100) / max);

    const recommendations = buckets
        .filter(bucket => bucket.earned < bucket.max)
        .map(({ key, section, field, variant, withoutAi, earned: bucketEarned, earnedAfterNextStep }, order) => ({
            key,
            section,
            field,
            variant,
            withoutAi,
            order,
            gain: earnedAfterNextStep - bucketEarned,
        }))
        .sort((a, b) => b.gain - a.gain || a.order - b.order)
        .map(({ key, section, field, variant, withoutAi }) => ({ key, section, field, variant, withoutAi }));

    return { earned, max, percent, recommendations };
};
